<template>
  <div class="SidebarSide" :class="{ isOpen }">
    <MainLogo type="white" />
    <nav>
      <template v-for="(link, index) in links">
        <MenuLink
          :key="index"
          :index="index"
          :link="link"
          @click="$emit('update:activeIndex', index)" />
        <transition-group :key="`${index}_transition`" appear name="slide-down">
          <div
            v-if="activeIndex === index && link.children && link.children.length"
            :key="`${index}_children`"
            class="children">
            <MenuLink
              v-for="(child, childIndex) in link.children"
              :key="`${index}_${childIndex}`"
              :index="childIndex"
              :link="child" />
          </div>
        </transition-group>
      </template>
    </nav>
    <div class="bottom">
      <div>
        {{ authUser | name }}
      </div>
      <Route data-cy="logout" to="Logout">
        <Icon name="lock" />
        {{ isOpen ? 'Logout' : '' }}
      </Route>
      <div
        class="collapse"
        @click="toggle"
        @keydown.enter="toggle">
        <Icon :name="isOpen ? 'left' : 'right'" />
        {{ isOpen ? 'Collapse' : '' }}
      </div>
    </div>
  </div>
</template>

<script>
import MainLogo from './MainLogo'
import MenuLink from './MenuLink'
import { getters } from '@utils'

export default {
  components: {
    MainLogo,
    MenuLink,
  },
  props: {
    isOpen: Boolean,
    links: { type: Array, default: () => [] },
    activeIndex: { type: Number, required: false },
  },
  computed: {
    ...getters('isDesktop'),
  },
  methods: {
    toggle() {
      this.$emit('update:isOpen', !this.isOpen)
    },
  },
}
</script>

<style lang="scss">
$_info-lightest: mix($white, $info-lighter, 85%);

.SidebarSide {
  @include trans(0.2s);
  display: flex;
  flex-direction: column;
  height: 100%;
  left: 0;
  overflow-x: hidden;
  overflow-y: scroll;
  position: fixed;
  top: 0;
  @include sm {
    width: $sidebar-width;
  }

  > .MainLogo {
    @include trans(0.2s);
    display: block;
    height: 3rem;
    margin: 2rem 0;
    text-align: center;
  }

  > nav {
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 1rem;
    width: 100%;

    .MenuLink {
      border-right: none;
      padding: unset;
      position: relative;
      z-index: 1;

      a {
        align-items: center;
        border-bottom: 0;
        color: $white;
        display: flex;
        font-weight: $semibold;
        height: 3.5rem;
        padding: 0 1rem 0 1.5rem;
        position: relative;
        text-align: left;
        white-space: nowrap;
        width: 100%;

        &::after {
          @include trans;
          background: transparent;
          bottom: 0;
          content: ' ';
          display: block;
          height: 1px;
          position: absolute;
          right: 0;
          width: 1px;
        }

        > span {
          @include trans;

          &.Icon {
            float: left;
            font-size: $h3;
            line-height: $lh;
          }

          &.label {
            display: inline-block;
            padding-left: 1.5rem;
          }
        }

        &.router-link-active {
          background: rgba($white, 0.08);
          color: $success;

          &::after {
            background: $success;
            border-radius: $radius 0 0 $radius;
            height: 100%;
            top: 0;
            width: 4px;
          }
        }
      }
    }

    .children {
      position: relative;

      .MenuLink {
        width: 100%;

        > a {
          padding-left: calc(1.5rem - 3px);
        }
      }
    }
  }

  > .bottom {
    color: $text-color-light;
    margin-top: auto;
    text-align: center;
    white-space: nowrap;
    width: 100%;

    > a {
      @include trans;
      border: 0;
      color: inherit;
      display: block;
      padding: 1rem;

      &:hover {
        background: $grey-lightest;
      }
    }

    > .collapse {
      cursor: pointer;
      padding: 1rem;

      &:hover {
        background: $grey-lightest;
      }
    }
  }

  &:not(.isOpen) {
    width: $sidebar-width-compact;

    .MainLogo {
      height: 2rem;
      margin-bottom: 2rem;
    }

    > nav {
      .MenuLink {
        a {
          text-align: center;

          > span {
            &.Icon {
              float: none;
            }

            &.label {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
